import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import i18n, { applyDocumentLocale } from './i18n'
import router from './router'
import './styles/main.css'
import 'bootstrap-icons/font/bootstrap-icons.css'
import { wirePwaInstall } from './composables/usePwaInstall'
import { initNativePush } from './composables/useNativePush'
import { reportNativeDiag } from './composables/useNativeSetup'

// Capture the PWA install prompt globally + early — the browser fires
// `beforeinstallprompt` on load, before any single view mounts.
wirePwaInstall()

// iOS app: if already signed in on launch, register for APNs (no-op in browser).
if (localStorage.getItem('hr_token')) { initNativePush(); reportNativeDiag() }

applyDocumentLocale()

createApp(App)
  .use(i18n)
  .use(createPinia())
  .use(router)
  .mount('#app')

// Fade out the inline launch splash once mounted. A small minimum keeps it from
// flashing on fast loads (the logo gets to fade in + breathe first).
const splash = document.getElementById('app-splash')
if (splash) {
  setTimeout(() => {
    splash.classList.add('hide')
    setTimeout(() => splash.remove(), 500)
  }, 700)
}

// Register service worker for PWA install + offline.
//
// When a new SW version installs (e.g. v2 → v3 after a deploy), the
// existing page stays controlled by the old one until the next
// navigation. That's the source of "I deployed but users still see old
// behavior". We force a soft reload as soon as a new SW takes control,
// which gets every open tab onto the new version automatically.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js')
      const promote = (w) => w && w.postMessage({ type: 'SKIP_WAITING' })

      // Case 1: a new SW is ALREADY waiting at register time (update landed
      // between sessions).
      if (reg.waiting) promote(reg.waiting)

      // Case 2 (the common post-deploy case the old code missed): the update is
      // discovered AFTER this load. Listen for the new worker reaching
      // "installed" while an old SW still controls the page, then activate it —
      // otherwise the new code sits "waiting" until every tab closes, leaving
      // the user on stale code (e.g. an old SW that cached failed responses →
      // "the save doesn't work").
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing
        if (!nw) return
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) promote(nw)
        })
      })

      // Proactively ask the browser to fetch a fresh sw.js on every load.
      reg.update().catch(() => {})

      // controllerchange fires when the active SW for this page changes,
      // which is exactly the moment we want to refresh. Guard the flag so
      // we don't loop on the first install (no prior controller).
      let refreshed = false
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshed) return
        refreshed = true
        window.location.reload()
      })
    } catch (e) {
      console.warn('SW failed:', e)
    }
  })
}
